<template>
    <div class="cd-page">
        <!-- Header -->
        <div class="cd-header">
            <button class="cd-back" @click="$router.push('/cost-requests')">
                <i class="fas fa-arrow-left"></i>
            </button>
            <div class="cd-header-info">
                <h1 class="cd-title">{{ request.title || "Cost Request" }}</h1>
                <div class="cd-header-meta">
                    <span class="cd-id">{{ request.requestNumber || ('#' + request.id) }}</span>
                    <span class="cd-status-pill" :style="statusStyle">{{ statusName }}</span>
                    <span class="cd-tier-pill" v-if="request.tier">{{ request.tier.name || ('T' + request.tier) }}</span>
                </div>
            </div>
            <div class="cd-header-actions">
                <!-- Edit (draft only) -->
                <button class="cd-btn cd-btn-outline" @click="$router.push(`/cost-requests/${request.id}/edit`)" v-if="canEdit">
                    <i class="fas fa-pen"></i> Edit
                </button>
                <!-- Submit (draft only) -->
                <button class="cd-btn cd-btn-primary" @click="submitRequest" v-if="canSubmit">
                    <i class="fas fa-paper-plane"></i> Submit
                </button>
                <!-- Approve (pending_approval, for approver) -->
                <button class="cd-btn cd-btn-success" @click="showApproveModal = true" v-if="canApprove">
                    <i class="fas fa-check"></i> Approve
                </button>
                <!-- Reject (pending_approval, for approver) -->
                <button class="cd-btn cd-btn-danger" @click="showRejectModal = true" v-if="canApprove">
                    <i class="fas fa-times"></i> Reject
                </button>
                <!-- Request Info (pending_approval, for approver) -->
                <button class="cd-btn cd-btn-info" @click="showInfoRequestModal = true" v-if="canApprove">
                    <i class="fas fa-question-circle"></i> Request Info
                </button>
                <!-- Provide Info (info_requested, for requester) -->
                <button class="cd-btn cd-btn-primary" @click="showProvideInfoModal = true" v-if="canProvideInfo">
                    <i class="fas fa-reply"></i> Provide Info
                </button>
                <!-- Settle (verified) -->
                <button class="cd-btn cd-btn-success" @click="showSettleModal = true" v-if="canSettle">
                    <i class="fas fa-check-double"></i> Settle
                </button>
                <!-- Cancel -->
                <button class="cd-btn cd-btn-outline cd-btn-cancel" @click="showCancelModal = true" v-if="canCancel">
                    <i class="fas fa-ban"></i> Cancel
                </button>
            </div>
        </div>

        <div class="cd-loading" v-if="loading">
            <i class="fas fa-circle-notch fa-spin"></i> Loading...
        </div>

        <div class="cd-layout" v-else>
            <!-- Left column -->
            <div class="cd-main">
                <!-- Amount Card -->
                <div class="cd-amount-card">
                    <div class="cd-amount-row">
                        <div class="cd-amount-block">
                            <span class="cd-amount-label">Requested Amount</span>
                            <span class="cd-amount-value">₹{{ formatAmount(request.amount) }}</span>
                        </div>
                        <div class="cd-amount-block" v-if="request.approvedAmount">
                            <span class="cd-amount-label">Approved Amount</span>
                            <span class="cd-amount-value cd-amount-green">₹{{ formatAmount(request.approvedAmount) }}</span>
                        </div>
                    </div>
                </div>

                <!-- Details Card -->
                <div class="cd-card">
                    <div class="cd-card-header">
                        <i class="fas fa-info-circle"></i> Request Details
                    </div>
                    <div class="cd-card-body">
                        <div class="cd-detail-grid">
                            <div class="cd-detail">
                                <span class="cd-detail-label">Category</span>
                                <span class="cd-detail-value">{{ request.category?.name || '—' }}</span>
                            </div>
                            <div class="cd-detail">
                                <span class="cd-detail-label">Priority</span>
                                <span class="cd-detail-value cd-prio-pill" :style="priorityStyle">{{ request.priority?.name || '—' }}</span>
                            </div>
                            <div class="cd-detail">
                                <span class="cd-detail-label">Request Type</span>
                                <span class="cd-detail-value">
                                    <i :class="request.requestType === 'pre_approval' ? 'fas fa-clipboard-check' : 'fas fa-receipt'" style="margin-right:4px; color:#005e89;"></i>
                                    {{ request.requestType === "pre_approval" ? "Pre-Approval" : "Reimbursement" }}
                                </span>
                            </div>
                            <div class="cd-detail">
                                <span class="cd-detail-label">Requested By</span>
                                <span class="cd-detail-value"><i class="fas fa-user" style="margin-right:4px; color:#94a3b8;"></i>{{ request.requestedBy?.fullName || '—' }}</span>
                            </div>
                            <div class="cd-detail" v-if="request.department">
                                <span class="cd-detail-label">Department</span>
                                <span class="cd-detail-value">{{ request.department?.name || request.department }}</span>
                            </div>
                            <div class="cd-detail" v-if="request.company">
                                <span class="cd-detail-label">Company</span>
                                <span class="cd-detail-value">{{ request.company?.name || request.company }}</span>
                            </div>
                            <div class="cd-detail">
                                <span class="cd-detail-label">Created</span>
                                <span class="cd-detail-value">{{ formatDate(request.createdAt) }}</span>
                            </div>
                            <div class="cd-detail" v-if="request.billDueDate">
                                <span class="cd-detail-label">Bill Due Date</span>
                                <span class="cd-detail-value" :class="{ 'cd-overdue': request.isOverdue }">{{ formatDate(request.billDueDate) }}</span>
                            </div>
                            <div class="cd-detail" v-if="request.approvedBy">
                                <span class="cd-detail-label">Approved By</span>
                                <span class="cd-detail-value">{{ request.approvedBy?.fullName || '—' }}</span>
                            </div>
                            <div class="cd-detail" v-if="request.approvedAt">
                                <span class="cd-detail-label">Approved On</span>
                                <span class="cd-detail-value">{{ formatDate(request.approvedAt) }}</span>
                            </div>
                            <div class="cd-detail" v-if="request.currentApprover">
                                <span class="cd-detail-label">Current Approver</span>
                                <span class="cd-detail-value"><i class="fas fa-user-check" style="margin-right:4px; color:#005e89;"></i>{{ request.currentApprover?.fullName }}</span>
                            </div>
                        </div>
                        <div class="cd-description" v-if="request.description">
                            <span class="cd-detail-label">Description</span>
                            <p class="cd-desc-text">{{ request.description }}</p>
                        </div>
                        <div class="cd-description" v-if="request.rejectionReason">
                            <span class="cd-detail-label" style="color:#dc2626;">Rejection Reason</span>
                            <p class="cd-desc-text" style="color:#dc2626;">{{ request.rejectionReason }}</p>
                        </div>
                        <div class="cd-description" v-if="request.notes">
                            <span class="cd-detail-label">Notes</span>
                            <p class="cd-desc-text">{{ request.notes }}</p>
                        </div>
                    </div>
                </div>

                <!-- Bills Card -->
                <div class="cd-card" v-if="bills.length || statusCode === 'bill_pending' || statusCode === 'bill_rejected'">
                    <div class="cd-card-header">
                        <i class="fas fa-receipt"></i> Bill Proofs ({{ bills.length }})
                        <button class="cd-upload-bill-btn" v-if="canUploadBill" @click="showBillUpload = !showBillUpload">
                            <i class="fas fa-upload"></i> Upload Bill
                        </button>
                    </div>
                    <div class="cd-card-body">
                        <!-- Bill Upload Form -->
                        <div class="cd-bill-upload-form" v-if="showBillUpload">
                            <div class="cd-detail-grid">
                                <div class="cd-field">
                                    <label class="cd-label">Bill Number</label>
                                    <input class="cd-input" v-model="billForm.billNumber" placeholder="INV-XXXXX" />
                                </div>
                                <div class="cd-field">
                                    <label class="cd-label">Bill Date</label>
                                    <input class="cd-input" type="date" v-model="billForm.billDate" />
                                </div>
                                <div class="cd-field">
                                    <label class="cd-label">Bill Amount</label>
                                    <input class="cd-input" type="number" v-model.number="billForm.billAmount" placeholder="0" />
                                </div>
                                <div class="cd-field">
                                    <label class="cd-label">Vendor Name</label>
                                    <input class="cd-input" v-model="billForm.vendorName" placeholder="Vendor name" />
                                </div>
                                <div class="cd-field">
                                    <label class="cd-label">GST Number</label>
                                    <input class="cd-input" v-model="billForm.gstNumber" placeholder="22AAAAA0000A1Z5" />
                                </div>
                                <div class="cd-field">
                                    <label class="cd-label">Bill File</label>
                                    <input type="file" ref="billFileInput" @change="handleBillFileSelect" accept=".pdf,.jpg,.jpeg,.png" />
                                </div>
                            </div>
                            <div class="cd-bill-upload-actions">
                                <button class="cd-btn cd-btn-outline" @click="showBillUpload = false">Cancel</button>
                                <button class="cd-btn cd-btn-primary" @click="uploadBill" :disabled="billUploading">
                                    <i class="fas fa-upload" v-if="!billUploading"></i>
                                    <i class="fas fa-circle-notch fa-spin" v-else></i>
                                    {{ billUploading ? 'Uploading...' : 'Upload Bill' }}
                                </button>
                            </div>
                        </div>

                        <!-- Existing Bills -->
                        <div v-for="bill of bills" :key="bill.id" class="cd-bill-item">
                            <div class="cd-bill-grid">
                                <div class="cd-detail" v-if="bill.billNumber">
                                    <span class="cd-detail-label">Bill Number</span>
                                    <span class="cd-detail-value">{{ bill.billNumber }}</span>
                                </div>
                                <div class="cd-detail" v-if="bill.billDate">
                                    <span class="cd-detail-label">Bill Date</span>
                                    <span class="cd-detail-value">{{ formatDate(bill.billDate) }}</span>
                                </div>
                                <div class="cd-detail" v-if="bill.vendorName">
                                    <span class="cd-detail-label">Vendor</span>
                                    <span class="cd-detail-value">{{ bill.vendorName }}</span>
                                </div>
                                <div class="cd-detail" v-if="bill.gstNumber">
                                    <span class="cd-detail-label">GST Number</span>
                                    <span class="cd-detail-value">{{ bill.gstNumber || '—' }}</span>
                                </div>
                                <div class="cd-detail" v-if="bill.billAmount">
                                    <span class="cd-detail-label">Bill Amount</span>
                                    <span class="cd-detail-value cd-amount-green">₹{{ formatAmount(bill.billAmount) }}</span>
                                </div>
                                <div class="cd-detail">
                                    <span class="cd-detail-label">Status</span>
                                    <span class="cd-detail-value cd-bill-status" :class="'cd-bs-' + (bill.status || 'pending')">
                                        {{ bill.status || 'Pending' }}
                                    </span>
                                </div>
                            </div>
                            <div class="cd-bill-file" v-if="bill.fileUrl">
                                <a :href="getFileUrl(bill.fileUrl)" target="_blank" class="cd-file-link">
                                    <i class="fas fa-file"></i> {{ bill.fileName || 'View Bill' }}
                                </a>
                            </div>
                            <div class="cd-bill-remarks" v-if="bill.remarks">
                                <span class="cd-detail-label">Remarks</span>
                                <p class="cd-desc-text">{{ bill.remarks }}</p>
                            </div>
                            <!-- Verify/Reject bill actions -->
                            <div class="cd-bill-actions" v-if="bill.status === 'uploaded' || bill.status === 'pending'">
                                <button class="cd-btn cd-btn-sm cd-btn-success" @click="verifyBill(bill)">
                                    <i class="fas fa-check"></i> Verify
                                </button>
                                <button class="cd-btn cd-btn-sm cd-btn-danger" @click="openBillReject(bill)">
                                    <i class="fas fa-times"></i> Reject Bill
                                </button>
                            </div>
                        </div>

                        <div class="cd-bill-empty" v-if="!bills.length && !showBillUpload">
                            <i class="fas fa-file-invoice"></i>
                            <span>No bill proof uploaded yet</span>
                        </div>
                    </div>
                </div>

                <!-- Attachments -->
                <div class="cd-card">
                    <div class="cd-card-header">
                        <i class="fas fa-paperclip"></i> Attachments ({{ attachments.length }})
                        <button class="cd-upload-bill-btn" @click="$refs.attachInput.click()" v-if="canEdit">
                            <i class="fas fa-plus"></i> Add
                        </button>
                        <input type="file" ref="attachInput" multiple style="display:none" @change="uploadAttachments" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" />
                    </div>
                    <div class="cd-card-body">
                        <div class="cd-file-list" v-if="attachments.length">
                            <div class="cd-file-item" v-for="f of attachments" :key="f.id">
                                <a :href="getFileUrl(f.fileUrl)" target="_blank" class="cd-file-link-row">
                                    <i class="fas fa-file"></i>
                                    <span class="cd-file-name">{{ f.fileName }}</span>
                                    <i class="fas fa-download cd-file-dl"></i>
                                </a>
                                <button class="cd-file-delete" v-if="canEdit" @click="deleteAttachment(f)">
                                    <i class="fas fa-trash"></i>
                                </button>
                            </div>
                        </div>
                        <div class="cd-bill-empty" v-else>
                            <i class="fas fa-paperclip"></i>
                            <span>No attachments</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Right: Timeline & sidebar -->
            <div class="cd-sidebar">
                <!-- Activity Log / Timeline -->
                <div class="cd-side-card" v-if="logs.length">
                    <div class="cd-side-title">Activity Timeline</div>
                    <div class="cd-log-list">
                        <div class="cd-log-item" v-for="log of logs" :key="log.id">
                            <div class="cd-log-icon" :class="'cd-log-' + log.action">
                                <i :class="logIcon(log.action)"></i>
                            </div>
                            <div class="cd-log-body">
                                <span class="cd-log-action">{{ log.user?.fullName || 'System' }} <strong>{{ log.action }}</strong></span>
                                <span class="cd-log-comment" v-if="log.comment">{{ log.comment }}</span>
                                <span class="cd-log-date">{{ formatDateTime(log.createdAt) }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Tier Info -->
                <div class="cd-side-card" v-if="request.tier">
                    <div class="cd-side-title">Approval Tier</div>
                    <div class="cd-tier-display">
                        <div class="cd-tier-badge">{{ request.tier.name || ('T' + request.tier) }}</div>
                        <div>
                            <div class="cd-tier-range" v-if="request.tier.minAmount !== undefined">
                                ₹{{ formatAmount(request.tier.minAmount) }} – {{ request.tier.maxAmount ? '₹' + formatAmount(request.tier.maxAmount) : 'Unlimited' }}
                            </div>
                            <div class="cd-tier-approver" v-if="request.tier.label">
                                <i class="fas fa-user-check"></i> {{ request.tier.label }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Approve Modal -->
        <div class="cd-modal-overlay" v-if="showApproveModal" @click.self="showApproveModal = false">
            <div class="cd-modal">
                <div class="cd-modal-header">
                    <span>Approve Request</span>
                    <button class="cd-modal-close" @click="showApproveModal = false"><i class="fas fa-times"></i></button>
                </div>
                <div class="cd-modal-body">
                    <label class="cd-label">Approved Amount</label>
                    <div class="cd-amount-input-wrap">
                        <span class="cd-amount-prefix">₹</span>
                        <input class="cd-input cd-input-amount" type="number" v-model.number="approveForm.approvedAmount" :placeholder="String(request.amount || 0)" />
                    </div>
                    <label class="cd-label" style="margin-top:12px;">Comment (optional)</label>
                    <textarea class="cd-textarea" v-model="approveForm.comment" rows="2" placeholder="Add a comment..."></textarea>
                </div>
                <div class="cd-modal-footer">
                    <button class="cd-btn cd-btn-outline" @click="showApproveModal = false">Cancel</button>
                    <button class="cd-btn cd-btn-success" @click="approveRequest" :disabled="actionLoading">
                        <i class="fas fa-check"></i> Approve
                    </button>
                </div>
            </div>
        </div>

        <!-- Reject Modal -->
        <div class="cd-modal-overlay" v-if="showRejectModal" @click.self="showRejectModal = false">
            <div class="cd-modal">
                <div class="cd-modal-header">
                    <span>Reject Request</span>
                    <button class="cd-modal-close" @click="showRejectModal = false"><i class="fas fa-times"></i></button>
                </div>
                <div class="cd-modal-body">
                    <label class="cd-label">Reason for rejection <span style="color:#ef4444;">*</span></label>
                    <textarea class="cd-textarea" v-model="rejectForm.rejectionReason" rows="3" placeholder="Provide a reason for rejecting this request..."></textarea>
                    <label class="cd-label" style="margin-top:12px;">Comment (optional)</label>
                    <textarea class="cd-textarea" v-model="rejectForm.comment" rows="2" placeholder="Additional comments..."></textarea>
                </div>
                <div class="cd-modal-footer">
                    <button class="cd-btn cd-btn-outline" @click="showRejectModal = false">Cancel</button>
                    <button class="cd-btn cd-btn-danger" @click="rejectRequest" :disabled="!rejectForm.rejectionReason.trim() || actionLoading">
                        <i class="fas fa-times"></i> Reject
                    </button>
                </div>
            </div>
        </div>

        <!-- Request Info Modal -->
        <div class="cd-modal-overlay" v-if="showInfoRequestModal" @click.self="showInfoRequestModal = false">
            <div class="cd-modal">
                <div class="cd-modal-header">
                    <span>Request More Information</span>
                    <button class="cd-modal-close" @click="showInfoRequestModal = false"><i class="fas fa-times"></i></button>
                </div>
                <div class="cd-modal-body">
                    <label class="cd-label">What information do you need? <span style="color:#ef4444;">*</span></label>
                    <textarea class="cd-textarea" v-model="infoRequestComment" rows="3" placeholder="Describe what additional information is needed..."></textarea>
                </div>
                <div class="cd-modal-footer">
                    <button class="cd-btn cd-btn-outline" @click="showInfoRequestModal = false">Cancel</button>
                    <button class="cd-btn cd-btn-info" @click="requestInfo" :disabled="!infoRequestComment.trim() || actionLoading">
                        <i class="fas fa-question-circle"></i> Request Info
                    </button>
                </div>
            </div>
        </div>

        <!-- Provide Info Modal -->
        <div class="cd-modal-overlay" v-if="showProvideInfoModal" @click.self="showProvideInfoModal = false">
            <div class="cd-modal">
                <div class="cd-modal-header">
                    <span>Provide Information</span>
                    <button class="cd-modal-close" @click="showProvideInfoModal = false"><i class="fas fa-times"></i></button>
                </div>
                <div class="cd-modal-body">
                    <label class="cd-label">Your response <span style="color:#ef4444;">*</span></label>
                    <textarea class="cd-textarea" v-model="provideInfoComment" rows="3" placeholder="Provide the requested information..."></textarea>
                </div>
                <div class="cd-modal-footer">
                    <button class="cd-btn cd-btn-outline" @click="showProvideInfoModal = false">Cancel</button>
                    <button class="cd-btn cd-btn-primary" @click="provideInfo" :disabled="!provideInfoComment.trim() || actionLoading">
                        <i class="fas fa-reply"></i> Submit
                    </button>
                </div>
            </div>
        </div>

        <!-- Cancel Modal -->
        <div class="cd-modal-overlay" v-if="showCancelModal" @click.self="showCancelModal = false">
            <div class="cd-modal">
                <div class="cd-modal-header">
                    <span>Cancel Request</span>
                    <button class="cd-modal-close" @click="showCancelModal = false"><i class="fas fa-times"></i></button>
                </div>
                <div class="cd-modal-body">
                    <label class="cd-label">Reason (optional)</label>
                    <textarea class="cd-textarea" v-model="cancelComment" rows="2" placeholder="Why are you cancelling this request?"></textarea>
                </div>
                <div class="cd-modal-footer">
                    <button class="cd-btn cd-btn-outline" @click="showCancelModal = false">Cancel</button>
                    <button class="cd-btn cd-btn-danger" @click="cancelRequest" :disabled="actionLoading">
                        <i class="fas fa-ban"></i> Confirm Cancel
                    </button>
                </div>
            </div>
        </div>

        <!-- Settle Modal -->
        <div class="cd-modal-overlay" v-if="showSettleModal" @click.self="showSettleModal = false">
            <div class="cd-modal">
                <div class="cd-modal-header">
                    <span>Settle Request</span>
                    <button class="cd-modal-close" @click="showSettleModal = false"><i class="fas fa-times"></i></button>
                </div>
                <div class="cd-modal-body">
                    <label class="cd-label">Settlement comment (optional)</label>
                    <textarea class="cd-textarea" v-model="settleComment" rows="2" placeholder="e.g. Payment processed via NEFT"></textarea>
                </div>
                <div class="cd-modal-footer">
                    <button class="cd-btn cd-btn-outline" @click="showSettleModal = false">Cancel</button>
                    <button class="cd-btn cd-btn-success" @click="settleRequest" :disabled="actionLoading">
                        <i class="fas fa-check-double"></i> Settle
                    </button>
                </div>
            </div>
        </div>

        <!-- Bill Reject Modal -->
        <div class="cd-modal-overlay" v-if="showBillRejectModal" @click.self="showBillRejectModal = false">
            <div class="cd-modal">
                <div class="cd-modal-header">
                    <span>Reject Bill</span>
                    <button class="cd-modal-close" @click="showBillRejectModal = false"><i class="fas fa-times"></i></button>
                </div>
                <div class="cd-modal-body">
                    <label class="cd-label">Remarks <span style="color:#ef4444;">*</span></label>
                    <textarea class="cd-textarea" v-model="billRejectRemarks" rows="3" placeholder="Reason for rejecting the bill..."></textarea>
                </div>
                <div class="cd-modal-footer">
                    <button class="cd-btn cd-btn-outline" @click="showBillRejectModal = false">Cancel</button>
                    <button class="cd-btn cd-btn-danger" @click="rejectBill" :disabled="!billRejectRemarks.trim() || actionLoading">
                        <i class="fas fa-times"></i> Reject Bill
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { http, serverURL } from "@/utils/http"

const API_BASE = "/cost-approval-requests"

export default {
    data() {
        return {
            request: {},
            attachments: [],
            bills: [],
            logs: [],
            loading: true,
            actionLoading: false,
            // Modals
            showApproveModal: false,
            showRejectModal: false,
            showInfoRequestModal: false,
            showProvideInfoModal: false,
            showCancelModal: false,
            showSettleModal: false,
            showBillUpload: false,
            showBillRejectModal: false,
            // Form data
            approveForm: { approvedAmount: null, comment: "" },
            rejectForm: { rejectionReason: "", comment: "" },
            infoRequestComment: "",
            provideInfoComment: "",
            cancelComment: "",
            settleComment: "",
            billForm: { billNumber: "", billDate: "", billAmount: null, vendorName: "", gstNumber: "" },
            billFile: null,
            billUploading: false,
            billRejectRemarks: "",
            selectedBillId: null,
        }
    },
    computed: {
        statusCode() {
            if (this.request.status && typeof this.request.status === "object") return this.request.status.code
            return this.request.status || ""
        },
        statusName() {
            if (this.request.status && typeof this.request.status === "object") return this.request.status.name
            return this.request.status || ""
        },
        statusStyle() {
            const s = this.request.status
            if (s && typeof s === "object" && s.color) {
                return { background: s.color + "20", color: s.color }
            }
            return {}
        },
        priorityStyle() {
            const p = this.request.priority
            if (p && typeof p === "object" && p.color) {
                return { background: p.color + "20", color: p.color }
            }
            return {}
        },
        canEdit() {
            return this.statusCode === "draft"
        },
        canSubmit() {
            return this.statusCode === "draft"
        },
        canApprove() {
            return this.statusCode === "pending_approval"
        },
        canProvideInfo() {
            return this.statusCode === "info_requested"
        },
        canSettle() {
            return this.statusCode === "verified"
        },
        canCancel() {
            return ["draft", "pending_approval", "info_requested"].includes(this.statusCode)
        },
        canUploadBill() {
            return ["bill_pending", "bill_rejected"].includes(this.statusCode)
        },
    },
    async mounted() {
        await this.fetchRequest()
    },
    methods: {
        formatAmount(val) {
            if (!val) return "0"
            return Number(val).toLocaleString("en-IN")
        },
        formatDate(d) {
            if (!d) return ""
            return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
        },
        formatDateTime(d) {
            if (!d) return ""
            return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
        },
        getFileUrl(url) {
            if (!url) return "#"
            if (url.startsWith("http")) return url
            return `${serverURL}/${url}`
        },
        logIcon(action) {
            const map = {
                created: "fas fa-plus-circle",
                submitted: "fas fa-paper-plane",
                approved: "fas fa-check",
                rejected: "fas fa-times",
                info_requested: "fas fa-question-circle",
                info_provided: "fas fa-reply",
                cancelled: "fas fa-ban",
                bill_uploaded: "fas fa-upload",
                bill_verified: "fas fa-check-double",
                bill_rejected: "fas fa-times-circle",
                settled: "fas fa-check-double",
            }
            return map[action] || "fas fa-circle"
        },
        async fetchRequest() {
            this.loading = true
            try {
                const res = await http.get(`${API_BASE}/${this.$route.params.id}`)
                const data = res.data.result || res.data || {}
                this.request = data
                this.attachments = data.attachments || []
                this.bills = data.bills || []
                this.logs = data.logs || []
                // Pre-fill approve amount
                this.approveForm.approvedAmount = data.amount
            } catch (err) {
                console.error("Error fetching request", err)
                this.$toast?.error?.("Failed to load request")
            }
            this.loading = false
        },
        // --- Lifecycle Actions ---
        async submitRequest() {
            this.actionLoading = true
            try {
                await http.patch(`${API_BASE}/${this.request.id}/submit`, {})
                this.$toast?.success?.("Request submitted for approval")
                await this.fetchRequest()
            } catch (err) {
                this.$toast?.error?.(err.response?.data?.message || "Failed to submit")
            }
            this.actionLoading = false
        },
        async approveRequest() {
            this.actionLoading = true
            try {
                await http.patch(`${API_BASE}/${this.request.id}/approve`, {
                    approvedAmount: this.approveForm.approvedAmount || this.request.amount,
                    comment: this.approveForm.comment || undefined,
                })
                this.showApproveModal = false
                this.$toast?.success?.("Request approved")
                await this.fetchRequest()
            } catch (err) {
                this.$toast?.error?.(err.response?.data?.message || "Failed to approve")
            }
            this.actionLoading = false
        },
        async rejectRequest() {
            this.actionLoading = true
            try {
                await http.patch(`${API_BASE}/${this.request.id}/reject`, {
                    rejectionReason: this.rejectForm.rejectionReason,
                    comment: this.rejectForm.comment || undefined,
                })
                this.showRejectModal = false
                this.$toast?.success?.("Request rejected")
                await this.fetchRequest()
            } catch (err) {
                this.$toast?.error?.(err.response?.data?.message || "Failed to reject")
            }
            this.actionLoading = false
        },
        async requestInfo() {
            this.actionLoading = true
            try {
                await http.patch(`${API_BASE}/${this.request.id}/request-info`, {
                    comment: this.infoRequestComment,
                })
                this.showInfoRequestModal = false
                this.infoRequestComment = ""
                this.$toast?.success?.("Information requested")
                await this.fetchRequest()
            } catch (err) {
                this.$toast?.error?.(err.response?.data?.message || "Failed to request info")
            }
            this.actionLoading = false
        },
        async provideInfo() {
            this.actionLoading = true
            try {
                await http.patch(`${API_BASE}/${this.request.id}/provide-info`, {
                    comment: this.provideInfoComment,
                })
                this.showProvideInfoModal = false
                this.provideInfoComment = ""
                this.$toast?.success?.("Information provided")
                await this.fetchRequest()
            } catch (err) {
                this.$toast?.error?.(err.response?.data?.message || "Failed to provide info")
            }
            this.actionLoading = false
        },
        async cancelRequest() {
            this.actionLoading = true
            try {
                await http.patch(`${API_BASE}/${this.request.id}/cancel`, {
                    comment: this.cancelComment || undefined,
                })
                this.showCancelModal = false
                this.$toast?.success?.("Request cancelled")
                await this.fetchRequest()
            } catch (err) {
                this.$toast?.error?.(err.response?.data?.message || "Failed to cancel")
            }
            this.actionLoading = false
        },
        async settleRequest() {
            this.actionLoading = true
            try {
                await http.patch(`${API_BASE}/${this.request.id}/settle`, {
                    comment: this.settleComment || undefined,
                })
                this.showSettleModal = false
                this.$toast?.success?.("Request settled")
                await this.fetchRequest()
            } catch (err) {
                this.$toast?.error?.(err.response?.data?.message || "Failed to settle")
            }
            this.actionLoading = false
        },
        // --- Bill Actions ---
        handleBillFileSelect(e) {
            this.billFile = e.target.files?.[0] || null
        },
        async uploadBill() {
            this.billUploading = true
            try {
                const fd = new FormData()
                if (this.billForm.billNumber) fd.append("billNumber", this.billForm.billNumber)
                if (this.billForm.billDate) fd.append("billDate", this.billForm.billDate)
                if (this.billForm.billAmount) fd.append("billAmount", this.billForm.billAmount)
                if (this.billForm.vendorName) fd.append("vendorName", this.billForm.vendorName)
                if (this.billForm.gstNumber) fd.append("gstNumber", this.billForm.gstNumber)
                if (this.billFile) fd.append("bill", this.billFile)

                await http.post(`${API_BASE}/${this.request.id}/bills`, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                })
                this.showBillUpload = false
                this.billForm = { billNumber: "", billDate: "", billAmount: null, vendorName: "", gstNumber: "" }
                this.billFile = null
                this.$toast?.success?.("Bill uploaded successfully")
                await this.fetchRequest()
            } catch (err) {
                this.$toast?.error?.(err.response?.data?.message || "Failed to upload bill")
            }
            this.billUploading = false
        },
        async verifyBill(bill) {
            this.actionLoading = true
            try {
                await http.patch(`/cost-approval-bills/${bill.id}/verify`, { remarks: "Verified" })
                this.$toast?.success?.("Bill verified")
                await this.fetchRequest()
            } catch (err) {
                this.$toast?.error?.(err.response?.data?.message || "Failed to verify bill")
            }
            this.actionLoading = false
        },
        openBillReject(bill) {
            this.selectedBillId = bill.id
            this.billRejectRemarks = ""
            this.showBillRejectModal = true
        },
        async rejectBill() {
            this.actionLoading = true
            try {
                await http.patch(`/cost-approval-bills/${this.selectedBillId}/reject`, {
                    remarks: this.billRejectRemarks,
                })
                this.showBillRejectModal = false
                this.$toast?.success?.("Bill rejected")
                await this.fetchRequest()
            } catch (err) {
                this.$toast?.error?.(err.response?.data?.message || "Failed to reject bill")
            }
            this.actionLoading = false
        },
        // --- Attachment Actions ---
        async uploadAttachments(e) {
            const files = Array.from(e.target.files || [])
            if (!files.length) return
            try {
                const fd = new FormData()
                files.forEach((f) => fd.append("attachments", f))
                await http.post(`${API_BASE}/${this.request.id}/attachments`, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                })
                this.$toast?.success?.("Attachment(s) uploaded")
                await this.fetchRequest()
            } catch (err) {
                this.$toast?.error?.(err.response?.data?.message || "Failed to upload attachments")
            }
            e.target.value = ""
        },
        async deleteAttachment(attachment) {
            if (!confirm("Delete this attachment?")) return
            try {
                await http.delete(`/cost-approval-attachments/${attachment.id}`)
                this.attachments = this.attachments.filter((a) => a.id !== attachment.id)
                this.$toast?.success?.("Attachment deleted")
            } catch (err) {
                this.$toast?.error?.(err.response?.data?.message || "Failed to delete attachment")
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.cd-page { padding: 20px; }

/* Header */
.cd-header { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; }
.cd-back { width: 36px; height: 36px; border-radius: 8px; border: 1px solid $gray200; background: white; display: flex; align-items: center; justify-content: center; cursor: pointer; color: $gray600; transition: all $transitionFast; flex-shrink: 0; &:hover { border-color: $primaryColor; color: $primaryColor; } }
.cd-header-info { flex: 1; }
.cd-title { font-size: 20px; font-weight: 700; color: $gray800; margin: 0; }
.cd-header-meta { display: flex; align-items: center; gap: 8px; margin-top: 4px; }
.cd-id { font-size: 12px; font-weight: 600; color: $gray400; }
.cd-status-pill { font-size: 11px; font-weight: 700; padding: 3px 10px; border-radius: $radiusFull; }
.cd-tier-pill { font-size: 10px; font-weight: 800; padding: 3px 8px; border-radius: 4px; color: white; background: $primaryColor; }
.cd-header-actions { display: flex; gap: 8px; flex-wrap: wrap; }

/* Buttons */
.cd-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; border: none; font-family: inherit; transition: all $transitionFast; &:disabled { opacity: 0.5; cursor: not-allowed; } }
.cd-btn-outline { background: white; border: 1px solid $gray200; color: $gray700; &:hover { border-color: $primaryColor; color: $primaryColor; } }
.cd-btn-primary { background: $primaryColor; color: white; &:hover { background: darken($primaryColor, 8%); } }
.cd-btn-success { background: #10b981; color: white; &:hover { background: #059669; } }
.cd-btn-danger { background: #ef4444; color: white; &:hover { background: #dc2626; } }
.cd-btn-info { background: #3b82f6; color: white; &:hover { background: #2563eb; } }
.cd-btn-cancel { color: #dc2626; border-color: #fca5a5; &:hover { background: #fef2f2; border-color: #ef4444; } }
.cd-btn-sm { padding: 5px 10px; font-size: 11px; border-radius: 6px; }

.cd-loading { display: flex; align-items: center; justify-content: center; gap: 8px; padding: 60px; font-size: 14px; color: $gray400; i { font-size: 18px; color: $primaryColor; } }

/* Layout */
.cd-layout { display: grid; grid-template-columns: 1fr 320px; gap: 20px; align-items: flex-start; }
@media (max-width: 1024px) { .cd-layout { grid-template-columns: 1fr; } }
.cd-main { display: flex; flex-direction: column; gap: 16px; }

/* Amount Card */
.cd-amount-card { background: white; border: 1px solid $gray100; border-radius: $radiusMd; padding: 20px; }
.cd-amount-row { display: flex; gap: 24px; flex-wrap: wrap; }
.cd-amount-block { flex: 1; min-width: 120px; }
.cd-amount-label { display: block; font-size: 11px; font-weight: 600; color: $gray400; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
.cd-amount-value { font-size: 24px; font-weight: 800; color: $gray900; }
.cd-amount-green { color: #10b981; }
.cd-amount-blue { color: #3b82f6; }

/* Card */
.cd-card { background: white; border: 1px solid $gray100; border-radius: $radiusMd; overflow: hidden; }
.cd-card-header { display: flex; align-items: center; gap: 8px; padding: 12px 16px; font-size: 13px; font-weight: 700; color: $gray700; border-bottom: 1px solid $gray100; i { color: $primaryColor; font-size: 13px; } }
.cd-card-body { padding: 16px; }
.cd-upload-bill-btn { margin-left: auto; background: none; border: 1px solid $primaryColor; color: $primaryColor; padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 4px; &:hover { background: $primaryLight; } }

/* Detail Grid */
.cd-detail-grid, .cd-bill-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.cd-detail { display: block; }
.cd-detail-label { display: block; font-size: 11px; font-weight: 600; color: $gray400; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 3px; }
.cd-detail-value { font-size: 13px; font-weight: 600; color: $gray800; }
.cd-prio-pill { display: inline-block; padding: 2px 8px; border-radius: $radiusFull; font-size: 11px; text-transform: capitalize; }
.cd-overdue { color: #dc2626; font-weight: 700; }
.cd-description { margin-top: 14px; padding-top: 14px; border-top: 1px solid $gray100; }
.cd-desc-text { font-size: 13px; color: $gray600; line-height: 1.6; margin: 6px 0 0; }

/* Form Inputs (used in bill upload & modals) */
.cd-field { display: flex; flex-direction: column; }
.cd-label { display: block; font-size: 12px; font-weight: 600; color: $gray600; margin-bottom: 6px; }
.cd-input { width: 100%; padding: 8px 12px; border: 1px solid $gray200; border-radius: 8px; font-size: 13px; font-family: inherit; outline: none; transition: border-color $transitionFast; &:focus { border-color: $primaryColor; } &::placeholder { color: $gray300; } }
.cd-textarea { width: 100%; padding: 9px 12px; border: 1px solid $gray200; border-radius: 8px; font-size: 13px; font-family: inherit; outline: none; resize: vertical; &:focus { border-color: $primaryColor; } }

/* Bill Upload Form */
.cd-bill-upload-form { padding: 14px; margin-bottom: 14px; background: $gray50; border: 1px dashed $gray200; border-radius: 8px; }
.cd-bill-upload-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 14px; }

/* Bill Items */
.cd-bill-item { padding: 14px 0; & + .cd-bill-item { border-top: 1px solid $gray100; } }
.cd-bill-file { margin-top: 10px; }
.cd-file-link { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; color: $primaryColor; text-decoration: none; font-weight: 500; &:hover { text-decoration: underline; } i { font-size: 13px; } }
.cd-bill-remarks { margin-top: 8px; }
.cd-bill-actions { display: flex; gap: 8px; margin-top: 10px; }
.cd-bill-empty { display: flex; align-items: center; gap: 8px; padding: 16px 0; color: $gray300; font-size: 13px; i { font-size: 18px; } }
.cd-bill-status { display: inline-block; padding: 2px 8px; border-radius: $radiusFull; font-size: 11px; font-weight: 600; text-transform: capitalize; }
.cd-bs-pending { background: #fef3c7; color: #d97706; }
.cd-bs-uploaded { background: #e0f2fe; color: #0284c7; }
.cd-bs-verified { background: #d1fae5; color: #059669; }
.cd-bs-rejected { background: #fee2e2; color: #dc2626; }

/* Attachments / Files */
.cd-file-list { display: flex; flex-direction: column; gap: 6px; }
.cd-file-item { display: flex; align-items: center; gap: 8px; padding: 8px 10px; background: $gray50; border-radius: 6px; transition: background $transitionFast; &:hover { background: $gray100; } }
.cd-file-link-row { display: flex; align-items: center; gap: 8px; flex: 1; text-decoration: none; color: inherit; }
.cd-file-name { flex: 1; font-size: 12.5px; color: $gray700; font-weight: 500; }
.cd-file-dl { color: $gray400; font-size: 12px; }
.cd-file-delete { background: none; border: none; color: $gray300; font-size: 12px; cursor: pointer; padding: 4px; border-radius: 4px; transition: all $transitionFast; &:hover { color: #dc2626; background: #fef2f2; } }

/* Sidebar */
.cd-sidebar { display: flex; flex-direction: column; gap: 14px; position: sticky; top: 20px; }
.cd-side-card { background: white; border: 1px solid $gray100; border-radius: $radiusMd; padding: 14px 16px; }
.cd-side-title { font-size: 12px; font-weight: 700; color: $gray500; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px; }

/* Activity Log */
.cd-log-list { display: flex; flex-direction: column; gap: 10px; }
.cd-log-item { display: flex; gap: 8px; }
.cd-log-icon { width: 28px; height: 28px; border-radius: $radiusFull; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 11px; }
.cd-log-created { background: #f1f5f9; color: #64748b; }
.cd-log-submitted { background: #e0f2fe; color: #0284c7; }
.cd-log-approved { background: #d1fae5; color: #059669; }
.cd-log-rejected { background: #fee2e2; color: #dc2626; }
.cd-log-info_requested { background: #fef3c7; color: #d97706; }
.cd-log-info_provided { background: #e0f2fe; color: #0284c7; }
.cd-log-cancelled { background: #f1f5f9; color: #64748b; }
.cd-log-bill_uploaded { background: #ede9fe; color: #7c3aed; }
.cd-log-bill_verified { background: #d1fae5; color: #059669; }
.cd-log-bill_rejected { background: #fee2e2; color: #dc2626; }
.cd-log-settled { background: #d1fae5; color: #047857; }
.cd-log-commented { background: #fef3c7; color: #d97706; }
.cd-log-body { flex: 1; }
.cd-log-action { font-size: 12.5px; color: $gray600; strong { color: $gray800; text-transform: capitalize; } }
.cd-log-comment { display: block; font-size: 12px; color: $gray500; margin-top: 2px; font-style: italic; }
.cd-log-date { display: block; font-size: 10.5px; color: $gray400; margin-top: 2px; }

/* Tier Display */
.cd-tier-display { display: flex; align-items: center; gap: 12px; }
.cd-tier-badge { width: 44px; height: 44px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 16px; font-weight: 800; color: white; flex-shrink: 0; background: $primaryColor; }
.cd-tier-range { font-size: 14px; font-weight: 700; color: $gray800; }
.cd-tier-approver { font-size: 12px; color: $gray500; margin-top: 2px; i { color: $primaryColor; margin-right: 3px; } }

/* Modal */
.cd-modal-overlay { position: fixed; inset: 0; z-index: 1000; background: rgba(0,0,0,.4); display: flex; align-items: center; justify-content: center; }
.cd-modal { background: white; border-radius: $radiusMd; width: 460px; max-width: 95vw; box-shadow: $shadowXl; }
.cd-modal-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 18px; border-bottom: 1px solid $gray100; font-size: 15px; font-weight: 700; color: $gray800; }
.cd-modal-close { background: none; border: none; color: $gray400; font-size: 16px; cursor: pointer; &:hover { color: $gray700; } }
.cd-modal-body { padding: 18px; }
.cd-modal-footer { display: flex; gap: 10px; justify-content: flex-end; padding: 14px 18px; border-top: 1px solid $gray100; }

/* Modal Amount Input */
.cd-amount-input-wrap { display: flex; align-items: center; border: 1px solid $gray200; border-radius: 8px; overflow: hidden; &:focus-within { border-color: $primaryColor; } }
.cd-amount-prefix { padding: 8px 10px; background: $gray50; color: $gray500; font-weight: 700; font-size: 14px; border-right: 1px solid $gray200; }
.cd-input-amount { border: none; flex: 1; padding: 8px 12px; font-size: 14px; font-family: inherit; outline: none; }
</style>
